# Route: /tracking/forms/:id
# View a tracking form + its fields and signature slots, with lifecycle
# actions (make-effective, deprecate) in the ⋯ overflow menu.
# Permission: TrackingForm:read (view) + TrackingForm:approve (lifecycle)
# Feature: F87 M1 (tracking-form detail — closes the dangling
# /tracking/forms/:id row-action target from form-list).

# signature_surface: the header's approve applies a signature (the approver is recorded on the
# version), and the approver confirms the version this page renders — its info, fields and
# signature slots. So nothing this page mounts is a customization target (Feature #1913).
# The shell around it is the layout's, and stays one.
page-tracking-form-detail:
  type: Page
  archetype: detail
  signature_surface: true
  meta:
    title: $t.tracking.forms.detail.meta_title
    route: /tracking/forms/:id
    layout: shell
    auth: required
  data:
    template:
      model: TrackingForm
      key: $route.id
      required: true
    # F92 FR-4 — version-history rows for this logical template, keyed on the
    # SAME $route.id as `template` above (see data-provider.cir.yaml's
    # TrackingFormVersions entry).
    versions:
      model: TrackingFormVersions[]
      key: $route.id
      refresh: on_mount
  spatial:
    display: flex
    direction: column
    gap: $spacing.xl
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
    responsive:
      sm:
        padding: $spacing.sm
        padding_inline: $spacing.sm
        gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - form-detail-header
    - form-detail-approval-note
    - form-detail-info
    - form-detail-fields
    - form-detail-signatures
    - form-version-history

# ── Header ───────────────────────────────────────────────────────────────────
# Lifecycle actions live in the ⋯ overflow menu per the design-system rule
# "lifecycle and destructive actions hide in a ⋯ overflow menu" (XP85-UX-003).
# The server enforces TrackingForm:approve for all six. Editing routes to
# the canonical YAML editor (/tracking/forms/:id/edit); the API rejects
# edits to non-Draft versions, surfaced as a toast.
#
# F92 FR-14 — each lifecycle action's visibility binds to a server-computed
# transition flag (template.flags.can*), never a hardcoded status, so any D10
# lifecycle profile renders correctly with no CIR change. The flags come from the
# detail DTO (models.cir.yaml TrackingForm.flags / VersionTransitionFlags);
# `visible_when` on an overflow item is honoured by the emitter's reactive guard,
# and the server still enforces the transition.
#
# The flags answer for the caller as well as for the version: each is false for
# a caller who does not hold the permission its route requires
# (VersionTransitionFlags, computed from CallerVersionGrants), so an Editor
# holding only :update is offered none of the four approve-gated items.
#
# Each item also carries an explicit `permission:` (MenuItem.Permission)
# naming the permission the API route requires (TrackingFormEndpoints.cs):
# submit_review on TrackingForm:update, new_version on :create, and
# approve/return_to_draft/make_effective/deprecate on :approve. It is the
# client-side statement of the same rule the flag already carries — the
# permission guard is innermost, visible_when outermost (MenuItem.cs).

form-detail-header:
  type: PageHeader
  back_route: /tracking/forms
  title_prefix: $t.tracking.forms.detail.title_prefix
  bind:
    content: template.displayName
  actions:
    - label: $t.tracking.forms.detail.btn_edit
      route: /tracking/forms/:id/edit
      params:
        id: $data.template.id
      color: info
      # Content is mutable only in Draft (the server refuses an update to any
      # later status), so Edit follows the same server-computed flag the
      # lifecycle actions use rather than being offered on a frozen version.
      visible_when: template.flags.canEdit
      permission: "TrackingForm:update"
    # F79 canonical /audit deep-link (screen-guide.md §6c): the forward channel
    # pre-applies the TrackingForm filter on load; the return channel gives
    # /audit a contextual Back to this detail page. M1 manual test pass
    # (Act 2.1) found this button still on the pre-F79 bare-route shape.
    - label: $t.tracking.forms.detail.btn_audit
      route: /audit
      query:
        entityType: TrackingForm
        entityId: $route.id
      return_context: origin_only
      variant: ghost
  overflow:
    - label: $t.tracking.forms.detail.btn_submit_review
      color: info
      visible_when: template.flags.canSubmitForReview
      permission: "TrackingForm:update"
      action:
        type: invoke
        model: TrackingForm
        operation: submit_review
    - label: $t.tracking.forms.detail.btn_approve
      color: go
      visible_when: template.flags.canApprove
      permission: "TrackingForm:approve"
      action:
        type: invoke
        model: TrackingForm
        operation: approve
    - label: $t.tracking.forms.detail.btn_return_to_draft
      color: info
      visible_when: template.flags.canReturnToDraft
      permission: "TrackingForm:approve"
      action:
        type: invoke
        model: TrackingForm
        operation: return_to_draft
    - label: $t.tracking.forms.detail.btn_make_effective
      color: go
      visible_when: template.flags.canMakeEffective
      permission: "TrackingForm:approve"
      action:
        type: invoke
        model: TrackingForm
        operation: make_effective
    # F92 M-5 — success copy: the "on_success/toast" mechanism (ActionSpec.
    # SuccessMessage, record-sign.cir.yaml's `success_message:`) is threaded
    # into the dispatched event ONLY by FormEmitter (XP85.Emitter.Lit/Forms/
    # FormEmitter.cs) — MenuItem (this overflow item's type) has no
    # SuccessMessage field (XP85.Core/Types/MenuItem.cs) and
    # RowMenuEmitter.GenerateMenuItemActionCode's "invoke" case dispatches
    # `xp85-action` with no successMessage key, so a post-success toast
    # cannot be wired from CIR YAML alone today. The `confirm:` dialog IS
    # fully wired for overflow items (MenuItem.Confirm), so the wayfinding
    # copy is surfaced there instead — the operator reads it before
    # dispatch, telling them where the new Draft will appear.
    - label: $t.tracking.forms.detail.btn_new_version
      color: info
      visible_when: template.flags.canCreateNewVersion
      permission: "TrackingForm:create"
      action:
        type: invoke
        model: TrackingForm
        operation: new_version
        confirm: $t.tracking.forms.detail.btn_new_version_confirm
    - label: $t.tracking.forms.detail.btn_deprecate
      color: caution
      visible_when: template.flags.canDeprecate
      permission: "TrackingForm:approve"
      action:
        type: invoke
        model: TrackingForm
        operation: deprecate
        confirm: $t.tracking.forms.detail.btn_deprecate_confirm

# ── Info card ────────────────────────────────────────────────────────────────

# J2 — the version's creator is not offered Approve; this says why, instead of leaving them to
# suspect a missing permission. Bound to the server's own reading (flags.approvalNeedsAnotherPerson),
# which is true only when the version awaits an approval the caller is excluded from giving.
form-detail-approval-note:
  type: Text
  content: $t.tracking.forms.detail.approval_needs_another_person
  visible_when: template.flags.approvalNeedsAnotherPerson
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

form-detail-info:
  type: Card
  spatial:
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    border: $border.default
    radius: $radius.md
  children:
    - form-info-grid

form-info-grid:
  type: Grid
  spatial:
    columns: label-value
    gap: $spacing.md
    responsive:
      sm:
        columns: 1
  children:
    - form-info-name-label
    - form-info-name-value
    - form-info-display-name-label
    - form-info-display-name-value
    - form-info-description-label
    - form-info-description-value
    - form-info-status-label
    - form-info-status-value
    - form-info-version-label
    - form-info-version-value
    - form-info-contexts-label
    - form-info-contexts-value
    - form-info-reason-required-label
    - form-info-reason-required-value

form-info-name-label:
  type: Text
  content: $t.tracking.forms.detail.label_name
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

form-info-name-value:
  type: Text
  bind:
    content: template.name
  tokens:
    font_size: $font.size.md
    font_family: $font.family.mono

form-info-display-name-label:
  type: Text
  content: $t.tracking.forms.detail.label_display_name
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

form-info-display-name-value:
  type: Text
  bind:
    content: template.displayName
  tokens:
    font_size: $font.size.md

form-info-description-label:
  type: Text
  content: $t.tracking.forms.detail.label_description
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

form-info-description-value:
  type: Text
  bind:
    content: template.description
  tokens:
    font_size: $font.size.md

form-info-status-label:
  type: Text
  content: $t.tracking.forms.detail.label_status
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

# F92 — five-state variant chain, same rationale as the ReasonVocabulary
# detail page's reason-info-status-value (Effective=go, Approved=info,
# Obsolete=caution, Draft/InReview fall through to the final else=info).
form-info-status-value:
  type: StatusBadge
  bind:
    content: template.versionStatus
    variant:
      when: "template.versionStatus == 'Effective'"
      value: go
      else:
        when: "template.versionStatus == 'Approved'"
        value: info
        else:
          when: "template.versionStatus == 'Obsolete'"
          value: caution
          else: info

form-info-version-label:
  type: Text
  content: $t.tracking.forms.detail.label_version
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

form-info-version-value:
  type: Text
  bind:
    content: template.versionNumber
  tokens:
    font_size: $font.size.md
    font_family: $font.family.mono

form-info-contexts-label:
  type: Text
  content: $t.tracking.forms.detail.label_contexts
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

form-info-contexts-value:
  type: Text
  bind:
    content: template.applicableContexts
  tokens:
    font_size: $font.size.md

form-info-reason-required-label:
  type: Text
  content: $t.tracking.forms.detail.label_reason_required
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

form-info-reason-required-value:
  type: Text
  bind:
    content: template.reasonRequired
  tokens:
    font_size: $font.size.md

# ── Fields table ─────────────────────────────────────────────────────────────

form-detail-fields:
  type: Card
  spatial:
    padding: $spacing.lg
    display: flex
    direction: column
    gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface-elevated
    border: $border.default
    radius: $radius.md
  children:
    - form-fields-title
    - form-fields-table

form-fields-title:
  type: Text
  content: $t.tracking.forms.detail.fields_title
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.lg
    font_family: $font.family.display

form-fields-table:
  type: DataTable
  bind:
    rows: template.fields
  empty: form-fields-empty
  columns:
    - key: label
      label: $t.tracking.forms.detail.col_field_label
      tokens:
        font_size: $font.size.sm
    - key: name
      label: $t.tracking.forms.detail.col_field_name
      tokens:
        font_family: $font.family.mono
        font_size: $font.size.sm
    - key: fieldType
      label: $t.tracking.forms.detail.col_field_type
      tokens:
        font_size: $font.size.sm
    - key: required
      label: $t.tracking.forms.detail.col_field_required
      tokens:
        font_size: $font.size.sm
  a11y:
    label: $t.tracking.forms.detail.fields_table_label
  tokens:
    border: $border.default
    radius: $radius.md

form-fields-empty:
  type: EmptyState
  content: $t.tracking.forms.detail.fields_empty
  body: $t.tracking.forms.detail.fields_empty_body
  tokens:
    color: $color_scheme.light.color.on-surface-muted
    font_size: $font.size.md
  spatial:
    padding: $spacing.md

# ── Signature slots table ────────────────────────────────────────────────────

form-detail-signatures:
  type: Card
  spatial:
    padding: $spacing.lg
    display: flex
    direction: column
    gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface-elevated
    border: $border.default
    radius: $radius.md
  children:
    - form-signatures-title
    - form-signatures-table

form-signatures-title:
  type: Text
  content: $t.tracking.forms.detail.signatures_title
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.lg
    font_family: $font.family.display

form-signatures-table:
  type: DataTable
  bind:
    rows: template.signatureSlots
  empty: form-signatures-empty
  columns:
    - key: slot
      label: $t.tracking.forms.detail.col_slot
      tokens:
        font_size: $font.size.sm
    - key: meaning
      label: $t.tracking.forms.detail.col_meaning
      tokens:
        font_size: $font.size.sm
    - key: roleRequired
      label: $t.tracking.forms.detail.col_role
      tokens:
        font_size: $font.size.sm
    - key: required
      label: $t.tracking.forms.detail.col_required
      tokens:
        font_size: $font.size.sm
  a11y:
    label: $t.tracking.forms.detail.signatures_table_label
  tokens:
    border: $border.default
    radius: $radius.md

form-signatures-empty:
  type: EmptyState
  content: $t.tracking.forms.detail.signatures_empty
  body: $t.tracking.forms.detail.signatures_empty_body
  tokens:
    color: $color_scheme.light.color.on-surface-muted
    font_size: $font.size.md
  spatial:
    padding: $spacing.md

# ── Version history ──────────────────────────────────────────────────────────
# F92 FR-4 — every version sharing this template's logical name, ordered by
# creation. Each row links to that version's own detail page (row.id).

form-version-history:
  type: Card
  spatial:
    padding: $spacing.lg
    display: flex
    direction: column
    gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface-elevated
    border: $border.default
    radius: $radius.md
  children:
    - form-version-history-title
    - form-version-history-table

form-version-history-title:
  type: Text
  content: $t.tracking.forms.detail.card_versions_title
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.lg
    font_family: $font.family.display

form-version-history-table:
  type: DataTable
  bind:
    rows: versions
  empty: form-version-history-empty
  row_action:
    type: navigate
    route: /tracking/forms/:id
    params:
      id: $row.id
  columns:
    - key: versionNumber
      label: $t.tracking.forms.detail.col_version_number
      tokens:
        font_family: $font.family.mono
        font_size: $font.size.sm
    - key: versionStatus
      label: $t.tracking.forms.detail.col_version_status
      display: badge
      variant:
        when: "$row.versionStatus == 'Effective'"
        value: go
        else:
          when: "$row.versionStatus == 'Approved'"
          value: info
          else:
            when: "$row.versionStatus == 'Obsolete'"
            value: caution
            else: info
    - key: createdAt
      label: $t.tracking.forms.detail.col_version_created_at
      format: compliance-datetime
      tokens:
        font_size: $font.size.sm
    # F92 M-4 — createdBy is resolved to a human display name server-side by
    # GetVersionHistoryHandlerBase (via the batched IAuditEnrichmentLookup, the
    # same resolver the audit + tracking read models use), falling back to the
    # raw write-time subject only when no profile resolves. So the column binds
    # createdBy directly and carries no `font_family: mono` (raw-id convention).
    - key: createdBy
      label: $t.tracking.forms.detail.col_version_created_by
      tokens:
        font_size: $font.size.sm
    - key: effectiveFrom
      label: $t.tracking.forms.detail.col_version_effective_from
      format: compliance-datetime
      tokens:
        font_size: $font.size.sm
  a11y:
    label: $t.tracking.forms.detail.versions_table_label
  tokens:
    border: $border.default
    radius: $radius.md

form-version-history-empty:
  type: EmptyState
  content: $t.tracking.forms.detail.versions_empty
  body: $t.tracking.forms.detail.versions_empty_body
  tokens:
    color: $color_scheme.light.color.on-surface-muted
    font_size: $font.size.md
  spatial:
    padding: $spacing.md
